import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api, getToken } from '../../api';
import LocationSearch from '../../components/LocationSearch';
import { compressImage } from '../../components/ProfilePrompt';
import { TOTAL_ONBOARDING_STEPS } from './constants';

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;
  const fileRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [form, setForm] = useState({
    name: '', age: '', location: '', bio: '',
    gender: '', orientation: '',
    height_ft: '', height_in: '', smoking: '', drugs: '', religion: '', diet: '',
    income_annual: '', net_worth: '', has_children: '', wants_children: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(!fromSettings);
  const [initialLocation, setInitialLocation] = useState('');

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    if (!fromSettings) return;
    api.get('/profile').then(p => {
      setInitialLocation(p.location || '');
      setForm({
        name: p.name || '',
        age: p.age ? String(p.age) : '',
        location: p.location || '',
        bio: p.bio || '',
        gender: p.gender || '',
        orientation: p.orientation || '',
        height_ft: p.height_inches ? String(Math.floor(p.height_inches / 12)) : '',
        height_in: p.height_inches ? String(p.height_inches % 12) : '',
        smoking: p.smoking || '',
        drugs: p.drugs || '',
        religion: p.religion || '',
        diet: p.diet || '',
        income_annual: p.income_annual ? String(p.income_annual) : '',
        net_worth: p.net_worth ? String(p.net_worth) : '',
        has_children: p.has_children === true ? 'yes' : p.has_children === false ? 'no' : '',
        wants_children: p.wants_children || '',
      });
      if (p.photo_url) setPhotoPreview(p.photo_url);
      setProfileLoaded(true);
    }).catch(() => setProfileLoaded(true));
  }, [navigate, fromSettings]);

  if (!getToken()) {
    navigate('/login');
    return null;
  }

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const preview = URL.createObjectURL(file);
    try {
      const base64 = await compressImage(file);
      const check = await api.post('/profile/photos/validate-main', { photo: base64 });
      if (!check.valid) {
        setError(check.reason || 'Your profile photo must clearly show your face. Please use a headshot or close portrait.');
        return;
      }
      setPhotoPreview(preview);
      setPhotoBase64(base64);
      setError('');
    } catch {
      setPhotoPreview(preview);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    document.activeElement?.blur();
    setError('');
    setLoading(true);
    try {
      await api.post('/profile', {
        name: form.name,
        age: parseInt(form.age) || null,
        location: form.location,
        bio: form.bio,
        gender: form.gender,
        orientation: form.orientation,
        ...(photoBase64 ? { photo_url: photoBase64 } : {}),
      });
      const prefPayload = {};
      const htIn = (parseInt(form.height_ft)||0)*12 + (parseInt(form.height_in)||0);
      if (htIn > 0) prefPayload.height_inches = htIn;
      if (form.smoking) prefPayload.smoking = form.smoking;
      if (form.drugs) prefPayload.drugs = form.drugs;
      if (form.religion) prefPayload.religion = form.religion;
      if (form.diet) prefPayload.diet = form.diet;
      if (form.income_annual) prefPayload.income_annual = parseInt(form.income_annual);
      if (form.net_worth) prefPayload.net_worth = parseInt(form.net_worth);
      if (form.has_children !== '') prefPayload.has_children = form.has_children === 'yes';
      if (form.wants_children) prefPayload.wants_children = form.wants_children;
      if (Object.keys(prefPayload).length > 0) await api.post('/profile/preferences', prefPayload).catch(() => {});
      navigate(fromSettings ? '/settings' : '/onboarding/birth');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!profileLoaded) {
    return <div style={{ height: '100vh', background: '#000' }} />;
  }

  return (
    <div className="auth-screen" style={{ paddingTop: 60 }}>
      {!fromSettings && <div className="step-indicator">1 of {TOTAL_ONBOARDING_STEPS}</div>}
      <div className="display-md">{fromSettings ? 'Edit your profile' : 'Who you are'}</div>

      <form style={{ marginTop: 40 }} onSubmit={handleSubmit}>
        {/* Photo upload */}
        <div className="field-group">
          <label className="field-label">Photo</label>
          <div
            className="photo-upload-area"
            style={{ maxWidth: 180 }}
            onClick={() => fileRef.current.click()}
          >
            {photoPreview && <img src={photoPreview} alt="preview" />}
            {!photoPreview && (
              <>
                <div className="photo-plus">+</div>
                <div className="photo-upload-label">Add photo</div>
              </>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhoto}
          />
        </div>

        <div className="field-group">
          <label className="field-label">Full name</label>
          <input
            className="field"
            type="text"
            placeholder="Your name"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            required
          />
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          <div className="field-group" style={{ flex: '0 0 100px' }}>
            <label className="field-label">Age</label>
            <input
              className="field"
              type="number"
              placeholder="—"
              min={18}
              max={99}
              value={form.age}
              onChange={e => set('age', e.target.value)}
              required
            />
          </div>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">City</label>
            <LocationSearch
              key={initialLocation}
              placeholder="Where you live"
              showCurrentLocation={true}
              initialValue={initialLocation}
              onSelect={({ display }) => set('location', display)}
            />
          </div>
        </div>

        <div className="field-group">
          <label className="field-label">Bio</label>
          <textarea
            className="field"
            placeholder="A few words about you"
            rows={3}
            maxLength={300}
            value={form.bio}
            onChange={e => set('bio', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">I am</label>
            <select className="field" value={form.gender} onChange={e => set('gender', e.target.value)} required>
              <option value="">—</option>
              <option value="woman">Woman</option>
              <option value="man">Man</option>
              <option value="nonbinary">Non-binary</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Looking for</label>
            <select className="field" value={form.orientation} onChange={e => set('orientation', e.target.value)} required>
              <option value="">—</option>
              <option value="women">Women</option>
              <option value="men">Men</option>
              <option value="everyone">Everyone</option>
            </select>
          </div>
        </div>

        {/* Height */}
        <div className="field-group">
          <label className="field-label">Height</label>
          <div style={{ display: 'flex', gap: 12 }}>
            <select className="field" value={form.height_ft} onChange={e => set('height_ft', e.target.value)} style={{ flex: 1 }}>
              <option value="">ft</option>
              {[4,5,6,7].map(f => <option key={f} value={f}>{f} ft</option>)}
            </select>
            <select className="field" value={form.height_in} onChange={e => set('height_in', e.target.value)} style={{ flex: 1 }}>
              <option value="">in</option>
              {[0,1,2,3,4,5,6,7,8,9,10,11].map(i => <option key={i} value={i}>{i} in</option>)}
            </select>
          </div>
        </div>

        {/* Lifestyle */}
        <div style={{ display: 'flex', gap: 20 }}>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Smoking</label>
            <select className="field" value={form.smoking} onChange={e => set('smoking', e.target.value)}>
              <option value="">—</option>
              <option value="never">Never</option>
              <option value="occasionally">Occasionally</option>
              <option value="regularly">Regularly</option>
            </select>
          </div>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Drugs</label>
            <select className="field" value={form.drugs} onChange={e => set('drugs', e.target.value)}>
              <option value="">—</option>
              <option value="never">Never</option>
              <option value="occasionally">Occasionally</option>
              <option value="regularly">Regularly</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Religion</label>
            <select className="field" value={form.religion} onChange={e => set('religion', e.target.value)}>
              <option value="">—</option>
              <option value="none">None / Secular</option>
              <option value="spiritual">Spiritual</option>
              <option value="christian">Christian</option>
              <option value="jewish">Jewish</option>
              <option value="muslim">Muslim</option>
              <option value="hindu">Hindu</option>
              <option value="buddhist">Buddhist</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Diet</label>
            <select className="field" value={form.diet} onChange={e => set('diet', e.target.value)}>
              <option value="">—</option>
              <option value="anything">Anything</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="vegan">Vegan</option>
              <option value="pescatarian">Pescatarian</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Annual income</label>
            <select className="field" value={form.income_annual} onChange={e => set('income_annual', e.target.value)}>
              <option value="">Prefer not to say</option>
              <option value="30000">Under $50K</option>
              <option value="75000">$50K–100K</option>
              <option value="150000">$100K–200K</option>
              <option value="350000">$200K–500K</option>
              <option value="750000">$500K+</option>
            </select>
          </div>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Net worth</label>
            <select className="field" value={form.net_worth} onChange={e => set('net_worth', e.target.value)}>
              <option value="">Prefer not to say</option>
              <option value="50000">Under $100K</option>
              <option value="300000">$100K–500K</option>
              <option value="750000">$500K–1M</option>
              <option value="3000000">$1M–5M</option>
              <option value="10000000">$5M+</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 20 }}>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Has children</label>
            <select className="field" value={form.has_children} onChange={e => set('has_children', e.target.value)}>
              <option value="">—</option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          <div className="field-group" style={{ flex: 1 }}>
            <label className="field-label">Wants children</label>
            <select className="field" value={form.wants_children} onChange={e => set('wants_children', e.target.value)}>
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="open">Open</option>
              <option value="enough">Already have enough</option>
            </select>
          </div>
        </div>

        {error && <div className="error-msg">{error}</div>}

        <div style={{ marginTop: 32 }}>
          <button className="btn btn-gold" type="submit" disabled={loading}>
            {loading ? <span className="spinner" /> : fromSettings ? 'Save & exit' : 'Continue'}
          </button>
        </div>
      </form>
    </div>
  );
}
